import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { PlannedQuestion } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { sourceChip, sourceQuote } from "./questionProvenance.js";
import { Plus, Alert, RefreshCw } from "../../components/icons.js";
import { X } from "lucide-react";

/**
 * Provenance fields (this wave) typed locally until the shared plannedQuestionSchema carries
 * them — they are OPTIONAL there too, so this intersection stays assignment-compatible in both
 * directions and collapses into a no-op once the shared build lands. Absent on every pre-wave
 * plan and on hand-added questions: no chip renders, which is the honest rendering of "we made
 * no claim". `sourcePhrase` is a phrase the API VERIFIED appears in the claimed source text.
 */
type ProvenancedQuestion = PlannedQuestion & {
  source?: "requirement" | "resume" | "standard";
  sourcePhrase?: string;
};

/* "Code reading" was right when software was the only vertical with an exercise. Since migration
   0057 a recruiter reviewing a nursing or retail plan sees this badge on a handover note or a shift
   rota, so it names what the question DOES rather than what the artefact used to be. */
const KIND_LABEL: Record<PlannedQuestion["kind"], string> = {
  behavioral: "Behavioral",
  // "Practical", not "Technical": a nurse's medication-safety question is practical, not IT
  // (#18 copy pass). The stored wire value stays `technical` — renaming it is a trap (verticals.ts).
  technical: "Practical",
  code: "Code",
  code_reading: "Read and explain",
};

const MAX_QUESTIONS = 40;

const newId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `tmp-${Math.random().toString(36).slice(2)}`;

const clean = (draft: ProvenancedQuestion[]): ProvenancedQuestion[] =>
  draft
    .map((q) => ({ ...q, prompt: q.prompt.trim() }))
    .filter((q) => q.prompt.length >= 4)
    .slice(0, MAX_QUESTIONS);

interface Props {
  requisitionId: string;
  /** The competencyIds chosen for this requisition — only sent to Regenerate (the generator plans
   *  against them). The editor itself shows no competency: a question is an evidence collector and
   *  the evaluator reads every answer against every competency. */
  competencies: string[];
  /** Bubbles the recruiter's edited set up on every change (parent PUTs it at send time).
   *  Never called on the initial load — only on a real edit — so an untouched plan stays untouched. */
  onDraftChange: (questions: PlannedQuestion[]) => void;
  /**
   * Called INSTEAD of onDraftChange when Regenerate succeeds. The generate endpoint already
   * stored the fresh set as source "generated" — bubbling it through onDraftChange made parents
   * PUT it right back, which stores it as "manual" and switches per-candidate tailoring OFF, the
   * opposite of what the Regenerate copy promises. Parents use this to clear their edited/dirty
   * state. Optional for back-compat: absent, the old (buggy) bubbling is preserved.
   */
  onRegenerated?: (questions: PlannedQuestion[], appliedTo: number) => void;
  /**
   * The parent's last-known edited draft (its onDraftChange buffer), if any. This component
   * UNMOUNTS whenever the wizard leaves the Review step (e.g. Back to add another resume) and
   * remounts on return — a fresh instance with no memory of what it showed before. Without this,
   * re-init always fell back to the server's cached plan, so an in-progress edit visually vanished
   * on every round trip even though the parent's own buffer still had it (and would still have
   * sent it) — indistinguishable, from the recruiter's seat, from the plan having regenerated.
   * Preferred over the server plan on init; absent/empty falls through to planQuery.data as before.
   */
  initialDraft?: PlannedQuestion[] | null;
}

/**
 * EMBEDDED question editor for the New Request review step. Loads the tailored plan (already
 * generated by the wizard on entering this step) as ONE flat, numbered list — every question has the
 * same layout and every question is scored, the opening one included — and lets the recruiter
 * edit / remove / add / regenerate. It owns NO navigation or send button — reviewing is OPTIONAL,
 * so the wizard drives sending and this component only reports edits via onDraftChange.
 */
export function QuestionPlanEditor({
  requisitionId,
  competencies,
  onDraftChange,
  onRegenerated,
  initialDraft,
}: Props): ReactNode {
  const [draft, setDraft] = useState<ProvenancedQuestion[]>([]);
  const didInit = useRef(false);
  // Plans stored before the opener became a normal scored question carry `unscored: true`. It is
  // ignored server-side now; dropping it here keeps the next save from writing it back.
  const normalise = (qs: ProvenancedQuestion[]): ProvenancedQuestion[] =>
    qs.map(({ unscored, ...q }) => (unscored ? { ...q, competencyId: null } : q));

  const planQuery = useQuery({
    queryKey: ["question-plan", requisitionId],
    queryFn: () => api.get<{ questions: ProvenancedQuestion[] }>(`/requisitions/${requisitionId}/questions`),
    enabled: Boolean(requisitionId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (didInit.current) return;
    // Reseed from the caller's held edit buffer first — a REMOUNT (Back to add another resume,
    // then Next again) must not look like the plan came back blank/regenerated when the recruiter
    // had already been editing it. Only a genuinely fresh editor (no prior edits this session)
    // falls through to the server's plan.
    if (initialDraft && initialDraft.length > 0) {
      setDraft(normalise(initialDraft));
      didInit.current = true;
      return;
    }
    if (planQuery.data) {
      setDraft(normalise(planQuery.data.questions ?? []));
      didInit.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planQuery.data, initialDraft]);

  // Apply an edit + bubble the cleaned result up (a real user edit, not the initial seed).
  const apply = (next: ProvenancedQuestion[]): void => {
    setDraft(next);
    onDraftChange(clean(next));
  };

  // What each question's prompt said when it arrived from the server — the baseline a REWRITE is
  // judged against. Kept across re-renders, refreshed whenever the server hands us a new plan.
  const loadedPromptsRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    if (planQuery.data) {
      loadedPromptsRef.current = new Map(
        (planQuery.data.questions ?? []).map((q) => [q.id, q.prompt.replace(/\s+/g, " ").trim()]),
      );
    }
  }, [planQuery.data]);

  const regen = useMutation({
    mutationFn: () =>
      api.post<{ questions: ProvenancedQuestion[]; appliedTo?: number }>(
        `/requisitions/${requisitionId}/questions/generate`,
        { competencies },
      ),
    onSuccess: (res) => {
      const next = normalise(res.questions ?? []);
      if (onRegenerated) {
        // The endpoint already stored this set as "generated" — update local state and let the
        // parent clear its dirty flag, WITHOUT the onDraftChange bubble that re-PUT it as manual.
        setDraft(next);
        loadedPromptsRef.current = new Map(next.map((q) => [q.id, q.prompt.replace(/\s+/g, " ").trim()]));
        onRegenerated(clean(next), res.appliedTo ?? 0);
      } else {
        apply(next);
      }
    },
  });

  // A REWRITTEN question loses its provenance. "From your requirement: <phrase>" is a claim about
  // the question the requirement produced — once the recruiter has changed the wording (beyond
  // whitespace) that claim describes a question that no longer exists, and showing it to the end
  // client would be a small lie in the exact feature built to earn their trust. Reorders, removals
  // and kind changes keep provenance; only changing the words clears it. Typing the original text
  // back restores it, which is the correct edge: the claim is true again.
  const updatePrompt = (id: string, prompt: string): void =>
    apply(
      draft.map((q) => {
        if (q.id !== id) return q;
        const baseline = loadedPromptsRef.current.get(id);
        const edited = baseline !== undefined && prompt.replace(/\s+/g, " ").trim() !== baseline;
        return edited
          ? { ...q, prompt, source: undefined, sourcePhrase: undefined }
          : { ...q, prompt, ...(baseline !== undefined ? restoreProvenance(planQuery.data?.questions, id) : {}) };
      }),
    );

  /** The server-verified provenance for a question, for restoring when an edit is typed back. */
  function restoreProvenance(
    loaded: ProvenancedQuestion[] | undefined,
    id: string,
  ): Pick<ProvenancedQuestion, "source" | "sourcePhrase"> {
    const orig = loaded?.find((q) => q.id === id);
    return { source: orig?.source, sourcePhrase: orig?.sourcePhrase };
  }
  const removeQuestion = (id: string): void => apply(draft.filter((q) => q.id !== id));

  const addQuestion = (): void => {
    if (draft.length >= MAX_QUESTIONS) return;
    // A hand-added question carries no competency: the evaluator reads every answer against every
    // competency, so there is nothing for the recruiter to pick (0100).
    apply([...draft, { id: newId(), competencyId: null, prompt: "", kind: "behavioral" }]);
  };

  const total = draft.length;
  const atMax = total >= MAX_QUESTIONS;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <span className="mut sm">
          {total}/{MAX_QUESTIONS} questions · Asha also asks adaptive follow-ups live
        </span>
        <button
          type="button"
          className="btn sm"
          disabled={regen.isPending || planQuery.isLoading}
          data-tip="Replace the list with a fresh AI-generated set"
          data-tip-pos="left"
          onClick={() => regen.mutate()}
        >
          {/* An outlined button with an icon — it read as plain text as a ghost button */}
          <RefreshCw aria-hidden="true" style={{ width: 14, height: 14 }} />
          {regen.isPending ? "Regenerating…" : "Regenerate"}
        </button>
      </div>

      {planQuery.isLoading && <p className="mut sm">Preparing tailored questions…</p>}

      {planQuery.isError && !planQuery.isLoading && (
        <div className="banner warn">
          <Alert />
          <div style={{ flex: 1 }}>
            Couldn't load the tailored questions right now — you can still send; the interview uses
            the standard set.
            <button
              className="btn ghost sm"
              style={{ marginLeft: 10 }}
              onClick={() => void planQuery.refetch()}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {!planQuery.isLoading && !planQuery.isError && (
        <>
          {draft.length === 0 && (
            <p className="mut sm" style={{ margin: "0 0 10px" }}>
              No questions yet — add one, or rely on adaptive follow-ups.
            </p>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {draft.map((q, i) => {
              // Provenance is context, not a control: a small chip beside the kind, and the
              // verified phrase as a one-line footnote UNDER the textarea — the recruiter's
              // job here is editing, and nothing may crowd that. Both render only when the
              // plan data carries them (old plans and hand-added questions carry neither).
              const chip = sourceChip(q.source);
              const quote = sourceQuote(q.source, q.sourcePhrase);
              return (
                <div key={q.id} className="t1-qcard">
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: 13.5 }}>Question {i + 1}</span>
                    <span className="pill pv" style={{ fontSize: 12 }}>
                      {KIND_LABEL[q.kind]}
                    </span>
                    {chip && (
                      <span className={chip.cls} style={{ fontSize: 12 }}>
                        {chip.label}
                      </span>
                    )}
                    <span style={{ flex: 1 }} />
                    <button
                      type="button"
                      className="t1-icon-btn"
                      aria-label="Remove question"
                      onClick={() => removeQuestion(q.id)}
                    >
                      <X aria-hidden="true" />
                    </button>
                  </div>
                  <textarea
                    className="rq-in"
                    value={q.prompt}
                    onChange={(e) => updatePrompt(q.id, e.target.value)}
                    maxLength={1000}
                    rows={3}
                    placeholder="Type the question the interviewer should ask…"
                    style={{ minHeight: 72 }}
                  />
                  {quote && (
                    <div
                      className="mut sm"
                      style={{
                        marginTop: 6,
                        fontStyle: "italic",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={quote}
                    >
                      {quote}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button type="button" className="btn ghost sm" style={{ marginTop: 12 }} disabled={atMax} onClick={addQuestion}>
            <Plus /> Add question
          </button>
        </>
      )}

      {regen.isError && (
        <p className="mut sm" style={{ margin: 0, color: "var(--crit)" }}>
          Couldn't regenerate — your current questions are unchanged.
        </p>
      )}
    </div>
  );
}
